import { createSlice } from '@reduxjs/toolkit';

const initialState = {
  // LGSP Logs list
  logs: [],
  loading: false,
  error: null,
  successMessage: null,
  // Pagination
  page: 1,
  pageSize: 10,
  total: 0,
  // Sorting
  sortColumn: 'requestTime',
  sortDirection: 'desc',
  // Filters
  filterUnitId: null,
  filterDeviceId: null,
  filterDeviceCode: '',
  filterStatus: null,
  filterIdentityNumber: '',
  filterStartDate: null,
  filterEndDate: null,
  // Init data
  initData: null,
  // Units for selection
  units: [],
  unitsLoading: false,
  // Devices for selected unit
  devices: [],
  devicesLoading: false,
  // Verify citizen
  verifyLoading: false,
  verifyResult: null,
  // Export
  exportLoading: false,
  // Report
  reportLoading: false,
  reportData: null,
  reportModalOpen: false,
};

const lgspIntegrationSlice = createSlice({
  name: 'lgspIntegration',
  initialState,
  reducers: {
    // Fetch LGSP logs
    fetchLogs: (state) => {
      state.loading = true;
      state.error = null;
    },
    fetchLogsSuccess: (state, action) => {
      state.loading = false;
      state.logs = action.payload.data;
      state.total = action.payload.meta.total;
      state.page = action.payload.meta.page;
      state.pageSize = action.payload.meta.size;
      state.error = null;
    },
    fetchLogsFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },

    // Fetch init data
    fetchInit: (state) => {
      state.loading = true;
      state.error = null;
    },
    fetchInitSuccess: (state, action) => {
      state.loading = false;
      state.initData = action.payload;
      state.error = null;
    },
    fetchInitFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },

    // Fetch units for selection
    fetchUnits: (state) => {
      state.unitsLoading = true;
      state.error = null;
    },
    fetchUnitsSuccess: (state, action) => {
      state.unitsLoading = false;
      state.units = action.payload.data || action.payload;
      state.error = null;
    },
    fetchUnitsFailure: (state, action) => {
      state.unitsLoading = false;
      state.error = action.payload;
    },

    // Fetch devices for selected unit
    fetchDevicesByUnit: (state) => {
      state.devicesLoading = true;
      state.devices = [];
      state.error = null;
    },
    fetchDevicesByUnitSuccess: (state, action) => {
      state.devicesLoading = false;
      // Filter devices that have allowLGSPAccess = true
      state.devices = action.payload.data || action.payload;
      state.error = null;
    },
    fetchDevicesByUnitFailure: (state, action) => {
      state.devicesLoading = false;
      state.error = action.payload;
    },

    // Verify citizen
    verifyCitizen: (state) => {
      state.verifyLoading = true;
      state.verifyResult = null;
      state.error = null;
    },
    verifyCitizenSuccess: (state, action) => {
      state.verifyLoading = false;
      state.verifyResult = action.payload;
      state.successMessage = 'lgspIntegration.verifySuccess';
      state.error = null;
    },
    verifyCitizenFailure: (state, action) => {
      state.verifyLoading = false;
      state.error = action.payload;
    },

    // Pagination
    setPage: (state, action) => {
      state.page = action.payload;
    },
    setPageSize: (state, action) => {
      state.pageSize = action.payload;
      state.page = 1;
    },

    // Sorting
    setSortColumn: (state, action) => {
      state.sortColumn = action.payload;
    },
    setSortDirection: (state, action) => {
      state.sortDirection = action.payload;
    },

    // Filters
    setFilterUnitId: (state, action) => {
      state.filterUnitId = action.payload;
      state.filterDeviceId = null; // Reset device when unit changes
      state.page = 1;
    },
    setFilterDeviceId: (state, action) => {
      state.filterDeviceId = action.payload;
      state.page = 1;
    },
    setFilterDeviceCode: (state, action) => {
      state.filterDeviceCode = action.payload;
      state.page = 1;
    },
    setFilterStatus: (state, action) => {
      state.filterStatus = action.payload;
      state.page = 1;
    },
    setFilterIdentityNumber: (state, action) => {
      state.filterIdentityNumber = action.payload;
      state.page = 1;
    },
    setFilterStartDate: (state, action) => {
      state.filterStartDate = action.payload;
      state.page = 1;
    },
    setFilterEndDate: (state, action) => {
      state.filterEndDate = action.payload;
      state.page = 1;
    },
    resetFilters: (state) => {
      state.filterUnitId = null;
      state.filterDeviceId = null;
      state.filterDeviceCode = '';
      state.filterStatus = null;
      state.filterIdentityNumber = '';
      state.filterStartDate = null;
      state.filterEndDate = null;
      state.page = 1;
    },

    // Export to Excel
    exportToExcel: (state) => {
      state.exportLoading = true;
      state.error = null;
    },
    exportToExcelSuccess: (state) => {
      state.exportLoading = false;
      state.successMessage = 'lgspIntegration.exportSuccess';
      state.error = null;
    },
    exportToExcelFailure: (state, action) => {
      state.exportLoading = false;
      state.error = action.payload;
    },

    // Get report
    fetchReport: (state) => {
      state.reportLoading = true;
      state.error = null;
    },
    fetchReportSuccess: (state, action) => {
      state.reportLoading = false;
      state.reportData = action.payload;
      state.error = null;
    },
    fetchReportFailure: (state, action) => {
      state.reportLoading = false;
      state.error = action.payload;
    },

    // Report modal
    setReportModalOpen: (state, action) => {
      state.reportModalOpen = action.payload;
    },
    clearReportData: (state) => {
      state.reportData = null;
    },

    // Clear messages
    clearError: (state) => {
      state.error = null;
    },
    clearSuccessMessage: (state) => {
      state.successMessage = null;
    },
    clearVerifyResult: (state) => {
      state.verifyResult = null;
    },
  },
});

export const {
  fetchLogs,
  fetchLogsSuccess,
  fetchLogsFailure,
  fetchInit,
  fetchInitSuccess,
  fetchInitFailure,
  fetchUnits,
  fetchUnitsSuccess,
  fetchUnitsFailure,
  fetchDevicesByUnit,
  fetchDevicesByUnitSuccess,
  fetchDevicesByUnitFailure,
  verifyCitizen,
  verifyCitizenSuccess,
  verifyCitizenFailure,
  exportToExcel,
  exportToExcelSuccess,
  exportToExcelFailure,
  fetchReport,
  fetchReportSuccess,
  fetchReportFailure,
  setReportModalOpen,
  clearReportData,
  setPage,
  setPageSize,
  setSortColumn,
  setSortDirection,
  setFilterUnitId,
  setFilterDeviceId,
  setFilterDeviceCode,
  setFilterStatus,
  setFilterIdentityNumber,
  setFilterStartDate,
  setFilterEndDate,
  resetFilters,
  clearError,
  clearSuccessMessage,
  clearVerifyResult,
} = lgspIntegrationSlice.actions;

// Selectors
export const selectLogs = (state) => state.lgspIntegration?.logs || [];
export const selectLoading = (state) => state.lgspIntegration?.loading || false;
export const selectError = (state) => state.lgspIntegration?.error;
export const selectSuccessMessage = (state) => state.lgspIntegration?.successMessage;
export const selectTotal = (state) => state.lgspIntegration?.total || 0;
export const selectPage = (state) => state.lgspIntegration?.page || 1;
export const selectPageSize = (state) => state.lgspIntegration?.pageSize || 10;
export const selectSortColumn = (state) => state.lgspIntegration?.sortColumn || 'requestTime';
export const selectSortDirection = (state) => state.lgspIntegration?.sortDirection || 'desc';
export const selectFilterUnitId = (state) => state.lgspIntegration?.filterUnitId;
export const selectFilterDeviceId = (state) => state.lgspIntegration?.filterDeviceId;
export const selectFilterDeviceCode = (state) => state.lgspIntegration?.filterDeviceCode || '';
export const selectFilterStatus = (state) => state.lgspIntegration?.filterStatus;
export const selectFilterIdentityNumber = (state) => state.lgspIntegration?.filterIdentityNumber || '';
export const selectFilterStartDate = (state) => state.lgspIntegration?.filterStartDate;
export const selectFilterEndDate = (state) => state.lgspIntegration?.filterEndDate;
export const selectInitData = (state) => state.lgspIntegration?.initData;
export const selectUnits = (state) => state.lgspIntegration?.units || [];
export const selectUnitsLoading = (state) => state.lgspIntegration?.unitsLoading || false;
export const selectDevices = (state) => state.lgspIntegration?.devices || [];
export const selectDevicesLoading = (state) => state.lgspIntegration?.devicesLoading || false;
export const selectVerifyLoading = (state) => state.lgspIntegration?.verifyLoading || false;
export const selectVerifyResult = (state) => state.lgspIntegration?.verifyResult;
export const selectExportLoading = (state) => state.lgspIntegration?.exportLoading || false;
export const selectReportLoading = (state) => state.lgspIntegration?.reportLoading || false;
export const selectReportData = (state) => state.lgspIntegration?.reportData;
export const selectReportModalOpen = (state) => state.lgspIntegration?.reportModalOpen || false;

export default lgspIntegrationSlice.reducer;
